import { Dialog } from '@angular/cdk/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT, Location, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ProductCategoriesComponent } from '../../products/product-categories/product-categories.component';
import { ProductSearchComponent } from '../../products/product-search/product-search.component';
import { SearchDialogComponent } from '../../shared/components/search-dialog/search-dialog.component';
import { AnalyticsLoaderService } from '../../shared/consent/analytics-loader.service';
import { ConsentService } from '../../shared/consent/consent.service';
import { ConsentBannerComponent } from '../../shared/consent/consent-banner.component';
import { legalFooterGateOpen, marketSwitcherLinks } from '../../shared/country/country.config';
import { CountryContext } from '../../shared/country/country-context.service';
import { LocaleService } from '../../shared/country/locale.service';
import { VendorOffer } from '../../shared/models/cart.models';
import { CartService } from '../../shared/services/cart.service';
import { CartSplitOptionsService } from '../../shared/services/cart-split-options.service';
import { SeoService } from '../../shared/services/seo.service';
import { ShippingPreferenceService } from '../../shared/services/shipping-preference.service';
import { vendorOfferTotal } from '../../shared/services/vendor-offer-total';
import { VendorRegistryService } from '../../shared/services/vendor-registry.service';
import { selectRailRows } from './cart-rail-rows';

// Inlined rather than registered by URL. addSvgIcon() defers an HttpClient GET to
// first render of the icon, and this one lives in the footer, which used to render
// on the affiliate interstitials while they were redirecting away. The aborted
// request surfaced as "Error retrieving icon :restnot!" through the global
// ErrorHandler. A literal has no request to abort. Content is verbatim
// src/assets/restnot.svg, which stays on disk for anything referencing it directly.
const RESTNOT_SVG =
  '<svg stroke="currentColor" fill="#fff" stroke-width="0" viewBox="0 0 512 512" focusable="false" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M217.4 27.43c-27.9.47-53.1 17.11-64.5 42.84l136.5 41.23c6-35.79-15.5-70.49-50.1-81.02-6.2-1.88-12.7-2.91-19.2-3.05h-2.7zm-69.7 60.08c-6.1 35.89 15.4 70.69 50.1 81.19 34.8 10.5 71.9-6.7 86.5-40zm265.5 44.29c-25.3.1-52.2 12.3-72.5 41L215.9 349.7c-33.5 47.4-18.9 97 14.1 120.4 33.1 23.5 84.6 20.8 118.1-26.6l124.7-176.8c33.5-47.5 18.9-97-14.1-120.5-12.4-8.8-27.3-13.9-43-14.4zm-1.8 17.3c1.3 0 2.6 0 3.8.1 12.1.5 23.5 4.8 33.1 11.7 25.7 18.2 38.6 54.5 9.7 95.4l-64.5 91.5c-35.8-9.6-81.8-42.3-102.7-73l64.7-91.6c16.9-23.9 37-33.7 55.9-34.1zM91.25 225.3c-9.62.1-19.11 2.1-27.93 6-33.11 14.5-50.34 51.5-40.24 86.3l130.72-57.1c-13.1-22.1-36.9-35.5-62.55-35.2zm69.65 51.6L30.2 334.1c18.45 31.4 57.3 44 90.6 29.5 33.2-14.6 50.4-51.8 40.1-86.7z"></path></svg>';

@Component({
  selector: 'app-consumer-layout',
  templateUrl: './consumer-layout.component.html',
  styleUrls: ['./consumer-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    RouterLink,
    NgOptimizedImage,
    RouterLinkActive,
    ProductSearchComponent,
    MatSidenavModule,
    ProductCategoriesComponent,
    MatBadgeModule,
    RouterOutlet,
    ConsentBannerComponent,
  ],
})
export class ConsumerLayoutComponent {
  private cartService = inject(CartService);
  protected router = inject(Router);
  private location = inject(Location);
  private seoService = inject(SeoService);
  private document = inject<Document>(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(Dialog);

  private breakpointObserver = inject(BreakpointObserver);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  readonly sidenav = viewChild.required<MatSidenav>('sidenav');
  private vendorRegistry = inject(VendorRegistryService);
  private splitOptions = inject(CartSplitOptionsService);
  private shippingPreference = inject(ShippingPreferenceService);
  private localeService = inject(LocaleService);
  private countryContext = inject(CountryContext);
  // Public: the template reads bannerOpen()/selfHosted and calls reopen().
  protected readonly consent = inject(ConsentService);
  protected readonly isSEMarket = this.countryContext.country === 'SE';
  protected readonly footerVariant = this.countryContext.config.footer.variant;
  // The identity and disclaimer links: withheld until a market has the articles.
  // The limited footer is filtered in country.config, but SE lists the pair inline
  // below, so its two anchors read the same switch. Display only: both pages still
  // resolve by direct URL everywhere.
  protected readonly showGatedLegalLinks = legalFooterGateOpen(this.countryContext.country);
  protected readonly footerLegalLinks = this.countryContext.config.footer.legalLinks;
  // The limited footer renders the legal links across two columns: a single stack
  // of them plus the cookie button towered over every other footer column.
  // Splitting here rather than in the template keeps the halves computed once,
  // and splits whatever the gate above left in the list.
  private readonly legalLinksSplit = Math.ceil(this.footerLegalLinks.length / 2);
  protected readonly footerLegalLinksFirst = this.footerLegalLinks.slice(0, this.legalLinksSplit);
  protected readonly footerLegalLinksSecond = this.footerLegalLinks.slice(this.legalLinksSplit);
  protected readonly marketLinks = marketSwitcherLinks(this.countryContext.country);
  protected readonly orgName = this.countryContext.config.siteSchema.organizationName;
  protected readonly scanEnabled = this.countryContext.isEnabled('barcodeScan');
  protected readonly guidesEnabled = this.countryContext.isEnabled('editorialGuides');
  protected readonly drugShortagesEnabled = this.countryContext.isEnabled('drugShortages');
  protected readonly toplistsEnabled = this.countryContext.country === 'SE';
  protected readonly campaignsPath = `/${this.countryContext.config.campaignsPath}`;
  protected expanded = signal(false);
  private hasProducts = computed(() => this.totalProducts() > 0);
  protected cartCollapsed = linkedSignal(() => !this.hasProducts());
  protected cartReady = signal(false);

  // Interactive-only signals (no layout role, only used when user opens sidenav)
  private isWideDesktop = signal(false);
  protected sidenavMode = signal<'over' | 'side'>('over');
  protected sidenavFixed = signal(true);
  private toolbarHeight = signal(58);
  protected sidenavTopGap = computed(() => {
    const base = this.toolbarHeight();
    return this.expanded() ? base * 2 : base;
  });

  protected apotekLogos = signal<any[]>([]);

  // Rendered twice back to back. The -50% keyframe lands the second copy exactly
  // where the first began, so the wrap is invisible and no edge is ever exposed.
  protected logoStrip = computed(() => {
    const logos = this.apotekLogos();
    return [...logos, ...logos];
  });

  protected currentUrl = signal<string>('');
  // Computed signals
  protected totalProducts = computed(() => this.cartService.cartSize());
  protected readonly includeShipping = this.shippingPreference.includeShipping;

  // AC-22: the shown options merged across store counts, then the warning
  // vendors in the offer map's order, five rows at most. AC-23 amends A7: the
  // rail prices the side the shopper picked on the cart or product page, so the
  // two cannot disagree. On the server the cart is empty, so the calculator
  // returns no lists.
  readonly railRows = computed(() =>
    selectRailRows(
      this.includeShipping()
        ? this.splitOptions.shippingLists()
        : this.splitOptions.productOnlyLists(),
      this.cartService.apotekTotal()
    )
  );

  protected isLandingPage = computed(() => this.currentUrl() === '/');
  protected isCartRoute = computed(() => this.currentUrl() === '/cart');
  protected isChatRoute = computed(() => this.currentUrl() === '/chat');
  protected isPortalRoute = computed(() => this.currentUrl().startsWith('/portal'));
  protected isLoginRoute = computed(() => this.currentUrl().startsWith('/login'));
  protected isAffiliateRoute = computed(() => this.currentUrl().startsWith('/go'));

  constructor() {
    // Instantiated for its constructor effect, which watches consent and injects
    // the market's Google tag once analytics is granted. Nothing calls into it, so this is what keeps
    // the lazily-created root service from never existing. Deliberately here and
    // not in app.config.ts: analytics belongs to the consumer app, and the brand
    // portal runs on its own layout.
    inject(AnalyticsLoaderService);
    this.seoService.setSiteJsonLd();
    this.iconRegistry.addSvgIconLiteral(
      'restnot',
      this.sanitizer.bypassSecurityTrustHtml(RESTNOT_SVG)
    );
    this.initializeApotekLogos();
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.url);

        // Homepage SEO is owned by DashboardComponent
      }
    });
    afterNextRender(() => {
      this.onActivate = () => {
        let top = this.document.getElementById('top');
        if (top !== null) {
          top.scrollIntoView(false);
          top = null;
        }
        setTimeout(() => {
          window.scroll({
            top: 0,
            left: 0,
            behavior: 'auto',
          });
        }, 100);
      };

      // BreakpointObserver only fires after hydration — manages interactive state only
      this.breakpointObserver
        .observe([
          Breakpoints.XSmall,
          Breakpoints.Small,
          Breakpoints.Medium,
          '(min-width: 1360px)',
          '(min-width: 1700px)',
        ])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          const isXSmall = result.breakpoints[Breakpoints.XSmall];
          const isSmall = result.breakpoints[Breakpoints.Small];
          const isWide = result.breakpoints['(min-width: 1360px)'];
          const isWideDesktop = result.breakpoints['(min-width: 1700px)'];

          if (isXSmall) {
            this.sidenavMode.set('over');
            this.sidenavFixed.set(true);
            this.toolbarHeight.set(58);
            this.isWideDesktop.set(false);
          } else if (isSmall || !isWide) {
            // 600px - 1359px
            this.sidenavMode.set('over');
            this.sidenavFixed.set(true);
            this.toolbarHeight.set(58);
            this.isWideDesktop.set(false);
          } else if (isWideDesktop) {
            // 1700px+
            this.sidenavMode.set('side');
            this.sidenavFixed.set(true);
            this.toolbarHeight.set(58);
            this.isWideDesktop.set(true);
          } else {
            // 1360px - 1699px
            this.sidenavMode.set('side');
            this.sidenavFixed.set(true);
            this.toolbarHeight.set(64);
            this.isWideDesktop.set(false);
          }

          // Enable CSS transitions after initial state is settled
          if (!this.cartReady()) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this.cartReady.set(true));
            });
          }
        });
    });
  }

  goToCart() {
    return this.router.navigate(['/cart']);
  }

  getImage(apoid: string): string | null {
    return this.vendorRegistry.getLogo(apoid);
  }

  getDisplayName(apoid: string): string {
    return this.vendorRegistry.getDisplayName(apoid);
  }

  openSearchDialog() {
    this.dialog.open(SearchDialogComponent, {
      minWidth: '100%',
      height: '100%',
    });
  }

  getSearch() {
    if (this.router.url.startsWith('/search')) {
      return this.router.url.split('/search/')[1].split('?')[0];
    } else {
      return '';
    }
  }

  stylePrice2(price?: number): string {
    return this.localeService.formatPriceAmount(price!);
  }

  protected warningPrice(offer: VendorOffer): number {
    return vendorOfferTotal(offer, this.includeShipping(), this.vendorRegistry);
  }

  currencySymbol(): string {
    return this.localeService.currencySymbol();
  }

  navigateToCart() {
    if (this.router.url === '/cart') {
      return this.location.back();
    } else {
      this.router.navigate(['/cart']);
    }
  }

  shut() {
    this.expanded.set(false);
    this.closeSidenav();
  }
  onActivate() {}
  toggle() {
    if (this.isWideDesktop()) return;
    this.expanded.set(false);
    this.sidenav().toggle();
  }
  closeSidenav() {
    if (!this.isWideDesktop()) {
      this.sidenav().close();
    }
  }
  toggleCart() {
    this.cartCollapsed.set(!this.cartCollapsed());
  }
  // Fifteen entries: the five that best match the cart, then ten more at random.
  // The count has to stay high enough that two copies of the strip overflow the
  // 1280px content column, which is what keeps the loop seamless.
  private initializeApotekLogos() {
    // .values(), not the Map itself: Array.from(map) yields [slug, offer] pairs,
    // and the template reads a.name off each entry.
    const allApoteks = Array.from(this.cartService.apotekTotal().values());
    const topFive = allApoteks.slice(0, 5);
    const remainingShuffled = allApoteks.slice(5).sort(() => Math.random() - 0.5);

    this.apotekLogos.set([...topFive, ...remainingShuffled.slice(0, 10)]);
  }
}
